import { createAsyncThunk } from '@reduxjs/toolkit';
import { notification } from 'antd';
import { fetchApiAuth } from '../utils/api';
import { CSVResponseProps, ResponseProps } from './types';
import { handleError, makeUrl } from '../utils/helpers';

export const dashboardVisitRequest = createAsyncThunk(
    'dashboard/visitRequest',
    async (payload, { rejectWithValue }) => {
        try {
            const response:ResponseProps = await fetchApiAuth({
                method: 'GET',
                url: 'visit-request/all',
            });
            return response.data;
        } catch (error:unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const createRequest = createAsyncThunk(
    'dashboard/createRequest',
    async (payload:{id: string|undefined; site: string; requester: string; status: string|undefined;}, { rejectWithValue }) => {
        const { id, site, requester, status } = payload;
        try {
            const response:ResponseProps = await fetchApiAuth({
                method: 'POST',
                url: 'visit-request/create',
                body: {
                    id,
                    site,
                    requester,
                    status,
                },
            });
            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

interface DashboardFilterPayload {
    q?: string | string[];
    site?: string[];
    status?: string[];
    startDate?: string;
    endDate?: string;
    page?: number;
    sort: string | null;
    sortField?: string | null;
    referenceNumber?: string[] | undefined;
    requesterName?: string[] | undefined;
    approverName?: string[] | undefined;
    dateSubmittedStart?: string | undefined;
    dateSubmittedEnd?: string | undefined;
    requestType?: string[] | undefined;
    requesterType?: string[] | undefined;
    /**
     * Ask for the page of rows only. The summary cards and filter options do
     * not change between pages, so paging skips recounting them.
     */
    listOnly?: boolean | undefined;
}

export const dashboardFilterRequest = createAsyncThunk(
    'dashboard/filterRequest',
    async (payload: DashboardFilterPayload, { rejectWithValue }) => {
        const { ...filterSearchUrl } = payload;
        const url = makeUrl('/visit-request/all', filterSearchUrl);
        try {
            const response:ResponseProps = await fetchApiAuth({
                method: 'GET',
                url,
            });
            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

// Helper function to delay execution
const delay = (ms: number): Promise<void> => new Promise((resolve) => {
    setTimeout(resolve, ms);
});

// Helper function to poll export status
const pollExportStatus = (exportId: string, maxAttempts = 60, intervalMs = 2000): Promise<string> => {
    const checkStatus = async (attempt: number): Promise<string> => {
        if (attempt >= maxAttempts) {
            throw new Error('Export timed out');
        }
        const statusResponse = await fetchApiAuth({
            method: 'GET',
            url: `export-status/${exportId}`,
        });
        const { status } = statusResponse.data as { status: string };
        if (status === 'completed') {
            return status;
        }
        if (status === 'failed') {
            throw new Error('Export failed');
        }
        await delay(intervalMs);
        return checkStatus(attempt + 1);
    };
    return checkStatus(0);
};

// Helper function to download export file
const downloadExportFile = async (exportId: string, filename: string, mimeType = 'application/pdf'): Promise<void> => {
    const response = await fetchApiAuth({
        method: 'GET',
        url: `export-download/${exportId}`,
        responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data as BlobPart], { type: mimeType }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

export const exportCompletedVisits = createAsyncThunk(
    'dashboard/exportCompletedVisits',
    async (payload: {
        q?: string | string[];
        site?: string[];
        status?: string[];
        startDate?: string;
        endDate?: string;
        exportType?: 'csv' | 'pdf';
        twoManSite?: string | undefined;
        includeAllBookings?: boolean | undefined;
    }, { rejectWithValue }) => {
        const { q, site, startDate, endDate, exportType = 'csv', twoManSite, includeAllBookings } = payload;
        try {
            const response: CSVResponseProps = await fetchApiAuth({
                method: 'POST',
                url: 'export-completed-visits',
                body: {
                    search: q,
                    site,
                    startDate,
                    endDate,
                    exportType,
                    twoManSite,
                    includeAllBookings,
                },
            });

            // Both CSV and PDF exports are async on the backend: the POST returns
            // { exportId } and the file is generated by a queued job. Poll for
            // completion, then download via the signed export-download endpoint.
            const responseData = response.data as unknown as { exportId: string };
            const { exportId } = responseData;
            await pollExportStatus(exportId);
            const filename = exportType === 'pdf' ? 'completed-visits.pdf' : 'completed-visits.csv';
            const mimeType = exportType === 'pdf' ? 'application/pdf' : 'text/csv';
            await downloadExportFile(exportId, filename, mimeType);
            return { success: true, exportId };
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);
